import { Button, Flex, Text, useToast } from '@chakra-ui/react'
import React from 'react'
import { HeadingPages } from '../../../../components/HeadingPages'
import { Navigation } from '../Navigation'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, FormProvider } from 'react-hook-form'
import { ListaPerguntasRespostas } from '../ListaPerguntasRespostas'
import { api } from '../../../../services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useEffect } from 'react'

async function onGetPerguntasRespostas() {
  const { data } = await api.get('/responde-crm/listar-respostas-faq?tipo=FAQ-ULTRARAPIDO')

  return data
}

async function onRemovePerguntaResposta({ id }) {
  const response = await api.delete(`/responde-crm/deletar-faq-responde/${id}`)

  return response.data
}

async function onRegister({ perguntas_respostas }) {
  const response = await api.post('/responde-crm/criar-faq-responde', {
    perguntas_respostas,
    tipo: 'FAQ-ULTRARAPIDO',
  })

  return response.data
}

const schema = z.object({
  perguntas_respostas: z
    .array(
      z.object({
        pergunta_id: z.string().optional(),
        pergunta: z.string().nonempty('A pergunta não pode ser vazia'),
        resposta: z
          .string()
          .nonempty('A resposta não pode ser vazia')
          .refine((value) => value.includes('<h3>'), { message: 'A resposta precisa conter uma tag <h3>' }),
      })
    )
    .nonempty('Adicione pelo menos uma pergunta e resposta'),
})

export const CadastroUltraRapido = () => {
  const form = useForm({
    resolver: zodResolver(schema),
  })

  const {
    handleSubmit,
    formState: { isDirty },
    setValue,
  } = form

  const toast = useToast({ position: 'top-right', isClosable: true })

  const queryClient = useQueryClient()

  const { mutateAsync, isLoading, error } = useMutation({
    mutationKey: 'register',
    mutationFn: onRegister,
    onSuccess: () => {
      toast({ title: 'Sucesso!', description: 'As perguntas e respostas foram cadastradas com sucesso! Logo estará no site para visualização' })

      queryClient.invalidateQueries('PerguntasERespostas-UltraRapido')
    },
  })

  const { data: perguntas_respostas = [] } = useQuery({
    queryKey: 'PerguntasERespostas-UltraRapido',
    queryFn: onGetPerguntasRespostas,
    onSuccess: (data) => {
      const formattedData = data.map((item) => {
        return {
          pergunta_id: item.id,
          pergunta: item.pergunta,
          resposta: item.resposta,
        }
      })

      setValue('perguntas_respostas', formattedData)
    },

    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24,
  })

  const { mutateAsync: deleteMutateAsync, isLoading: deleteLoading } = useMutation({
    mutationKey: 'remove',
    mutationFn: onRemovePerguntaResposta,
    onSuccess: () => {
      queryClient.invalidateQueries('PerguntasERespostas-UltraRapido')
    },
    onError: () => {
      toast({
        title: 'Erro ao remover pergunta e resposta',
        status: 'error',
      })
    },
  })

  useEffect(() => {
    if (perguntas_respostas) {
      const formattedData = perguntas_respostas.map((item) => {
        return {
          pergunta_id: item.id,
          pergunta: item.pergunta,
          resposta: item.resposta,
        }
      })

      setValue('perguntas_respostas', formattedData)
    }
  }, [perguntas_respostas, setValue])

  async function handleRegister(data) {
    await mutateAsync(data)
  }

  if (error) {
    return (
      <Text>
        Error: {error.message} - {error.status}
      </Text>
    )
  }

  return (
    <>
      <HeadingPages name="Cadastro de Perguntas e Respostas - Ultra Rápido" />
      <FormProvider {...form}>
        <fieldset disabled={isLoading}>
          <Flex flexDir="column" w="full" maxW="container.lg" mx="auto" gap={2} as="form" onSubmit={handleSubmit(handleRegister)}>
            <Navigation />
            <Text fontSize="2xl" fontWeight="bold" textAlign="center">
              Cadastro Perguntas e Respostas
            </Text>

            <Text textAlign="center" fontSize="sm" color="gray.500" mx="auto">
              Adicione ou remova perguntas e respostas para serem exibidas na página de ultra rápido do site.
            </Text>

            <ListaPerguntasRespostas onDelete={deleteMutateAsync} />

            {form.formState.errors.perguntas_respostas && (
              <Text color="red.500" textAlign="center">
                {form.formState.errors.perguntas_respostas.message}
              </Text>
            )}

            <Button alignSelf="flex-start" isLoading={isLoading} disabled={!isDirty} colorScheme="blue" size="sm" type="submit">
              Enviar
            </Button>
          </Flex>
        </fieldset>
      </FormProvider>
    </>
  )
}
